import { PaymentTerms, RequiredColumnsEnum, BillingCycleUnit, PURCHASE_ORDER_DOCUMENT_TYPE } from '@received/pricing-model';
import dayjs from 'dayjs';
import { Contract, PricingModelTab } from '../../../../types/contractTypes';
import { Customer } from '../../../../types/customerTypes';
import { PurchaseOrderData } from './PurchaseOrder.types';

export interface POLineItem {
	name: string;
	description?: string;
	quantity: number;
	unitPrice: number;
	total: number;
	billingStartDate?: Date | null;
	billingEndDate?: Date | null;
	duration?: string;
	billingCycleUnit?: BillingCycleUnit;
}

export const billingCycleShortLabels: Record<BillingCycleUnit, string> = {
	[BillingCycleUnit.ONE_TIME]: '',
	[BillingCycleUnit.WEEKLY]: 'wk.',
	[BillingCycleUnit.MONTHLY]: 'mo.',
	[BillingCycleUnit.QUARTERLY]: 'qtr.',
	[BillingCycleUnit.YEARLY]: 'yr.',
	[BillingCycleUnit.CUSTOM]: '',
};

export const formatPriceWithBillingCycle = (value: number | undefined, currencySign: string, billingCycleUnit?: BillingCycleUnit): string => {
	if (value === undefined || value === null || value === 0) {
		return '-';
	}
	const formattedValue = `${currencySign}${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
	if (!billingCycleUnit || billingCycleUnit === BillingCycleUnit.ONE_TIME || billingCycleUnit === BillingCycleUnit.CUSTOM) {
		return formattedValue;
	}
	return `${formattedValue} / ${billingCycleShortLabels[billingCycleUnit]}`;
};

const calculateDuration = (startDate?: Date | null, endDate?: Date | null, billingCycleUnit?: BillingCycleUnit): string => {
	if (!startDate || !endDate) return '';

	const start = dayjs(startDate);
	// Add 1 day to include both first and last day in the calculation
	const end = dayjs(endDate).add(1, 'day');
	const totalMonths = end.diff(start, 'month');

	const years = Math.floor(totalMonths / 12);
	const months = totalMonths % 12;

	if (billingCycleUnit === BillingCycleUnit.YEARLY || years > 0) {
		if (months === 0) {
			return years === 1 ? '1 year' : `${years} years`;
		}
		const yearPart = years === 1 ? '1 year' : `${years} years`;
		const monthPart = months === 1 ? '1 month' : `${months} months`;
		return `${yearPart} ${monthPart}`;
	}

	return totalMonths === 1 ? '1 month' : `${totalMonths} months`;
};

export interface POTotals {
	subTotal: number;
	discount: number;
	tax: number;
	total: number;
}

const hasCountQuantityColumn = (tab: PricingModelTab): boolean => {
	const columns = tab.pricingModelTable?.columns || [];
	const quantityColumn = columns.find(
		(col) =>
			col.id === RequiredColumnsEnum.quantity ||
			col.id === 'quantity' ||
			col.title?.toLowerCase() === 'quantity' ||
			col.title?.toLowerCase() === 'qty',
	);
	return quantityColumn?.type === 'Count';
};

const getRowLineItems = (tab: PricingModelTab): POLineItem[] => {
	const productName = tab.productName || tab.name || '';
	const rows = tab.pricingModelTable?.rows || [];

	return rows
		.filter((row) => !row.isHidden)
		.map((row) => {
			// Try to get item name from cell first, then fallback to itemPrice.item.name
			const itemCell = row.cells?.[RequiredColumnsEnum.item];
			const itemName = String(itemCell?.cellValue || '') || row.itemPrice?.item?.name || '';
			const quantity = Number(row.cells?.[RequiredColumnsEnum.quantity]?.cellValue) || 1;
			const unitPrice = Number(row.cells?.[RequiredColumnsEnum.price]?.cellValue) || 0;
			const total = Number(row.cells?.[RequiredColumnsEnum.amount]?.cellValue) || quantity * unitPrice;

			return {
				name: itemName ? `${productName} - ${itemName}` : productName,
				description: tab.billingCycle?.unit ? `Billing: ${tab.billingCycle.unit}` : undefined,
				quantity,
				unitPrice,
				total,
				billingStartDate: tab.billingStartDate,
				billingEndDate: tab.billingEndDate,
				duration: calculateDuration(tab.billingStartDate, tab.billingEndDate, tab.billingCycle?.unit),
				billingCycleUnit: tab.billingCycle?.unit,
			};
		});
};

export const getLineItems = (tabs?: PricingModelTab[]): POLineItem[] => {
	if (!tabs?.length) return [];

	const lineItems: POLineItem[] = [];

	tabs.forEach((tab) => {
		if (hasCountQuantityColumn(tab)) {
			// Product with Number quantity column - add each row as a line item
			lineItems.push(...getRowLineItems(tab));
		} else {
			// Default: one line with quantity = 1, no billing cycle shown on price/total
			const subTotal = tab.subTotal || 0;
			lineItems.push({
				name: tab.productName || tab.name || '',
				description: tab.billingCycle?.unit ? `Billing: ${tab.billingCycle.unit}` : undefined,
				quantity: 1,
				unitPrice: subTotal,
				total: subTotal,
				billingStartDate: tab.billingStartDate,
				billingEndDate: tab.billingEndDate,
				duration: calculateDuration(tab.billingStartDate, tab.billingEndDate, tab.billingCycle?.unit),
			});
		}
	});

	return lineItems;
};

export const getOrderTotals = (tabs?: PricingModelTab[]): POTotals => {
	if (!tabs?.length) {
		return {
			subTotal: 0,
			discount: 0,
			tax: 0,
			total: 0,
		};
	}

	let subTotal = 0;
	let totalDiscount = 0;
	let totalTax = 0;

	tabs.forEach((tab) => {
		const tabSubTotal = tab.subTotal || 0;
		const discountPercent = tab.discount || 0;
		const taxPercent = tab.tax || 0;

		const discountValue = (tabSubTotal * discountPercent) / 100;
		const afterDiscount = tabSubTotal - discountValue;
		const taxValue = (afterDiscount * taxPercent) / 100;

		subTotal += tabSubTotal;
		totalDiscount += discountValue;
		totalTax += taxValue;
	});

	const total = subTotal - totalDiscount + totalTax;

	return {
		subTotal,
		discount: totalDiscount,
		tax: totalTax,
		total,
	};
};

export const getPaymentTermsLabel = (netTerms: number | PaymentTerms): string => {
	if (netTerms === PaymentTerms.DUE_UPON_RECEIPT) {
		return 'Due upon receipt';
	}
	if (netTerms === PaymentTerms.DUE_UPON_USAGE) {
		return 'Due upon usage';
	}
	return `Net ${netTerms}`;
};

export interface ProductPaymentTerms {
	productName: string;
	netTerms?: number | PaymentTerms;
	billingCycleUnit?: BillingCycleUnit;
}

export interface PaymentTermsInfo {
	isIdentical: boolean;
	// If identical, use these values
	netTerms?: number | PaymentTerms;
	billingCycleUnit?: BillingCycleUnit;
	// If not identical, use per-product terms
	productTerms: ProductPaymentTerms[];
}

export const getPaymentTermsInfo = (tabs?: PricingModelTab[]): PaymentTermsInfo => {
	if (!tabs?.length) {
		return { isIdentical: true, productTerms: [] };
	}

	const productTerms: ProductPaymentTerms[] = tabs.map((tab) => ({
		productName: tab.productName || tab.name || '',
		netTerms: tab.netTerms,
		billingCycleUnit: tab.billingCycle?.unit,
	}));

	// Check if all products have identical terms
	const firstNetTerms = tabs[0].netTerms;
	const firstBillingCycle = tabs[0].billingCycle?.unit;

	const isIdentical = tabs.every((tab) => tab.netTerms === firstNetTerms && tab.billingCycle?.unit === firstBillingCycle);

	return {
		isIdentical,
		netTerms: firstNetTerms,
		billingCycleUnit: firstBillingCycle,
		productTerms,
	};
};

export interface BuildPurchaseOrderDataParams {
	contract: Contract;
	customer?: Customer;
	supplier?: Customer;
	currencySign: string;
	lineItems: POLineItem[];
	totals: POTotals;
	paymentTermsInfo: PaymentTermsInfo;
}

export const buildPurchaseOrderData = ({
	contract,
	customer,
	supplier,
	currencySign,
	lineItems,
	totals,
	paymentTermsInfo,
}: BuildPurchaseOrderDataParams): PurchaseOrderData => ({
	accountingEventType: PURCHASE_ORDER_DOCUMENT_TYPE,
	poNumber: contract?.poNumber,
	contractNumber: contract?.contractNumber,
	issueDate: contract?.createdAt,
	buyer: customer
		? {
				name: customer.name,
				legalName: customer.legalName,
				billingAddress: customer.billingAddress,
				ein: customer.ein,
		  }
		: undefined,
	seller: supplier
		? {
				name: supplier.name,
				legalName: supplier.legalName,
				billingAddress: supplier.billingAddress,
				ein: supplier.ein,
				iconData: supplier.iconData,
				email: supplier.email,
		  }
		: undefined,
	activationStartDate: contract?.activationStartDate,
	activationEndDate: contract?.activationEndDate,
	currencySign,
	lineItems: lineItems.map((item) => ({
		name: item.name,
		description: item.description,
		quantity: item.quantity,
		unitPrice: item.unitPrice,
		total: item.total,
		billingStartDate: item.billingStartDate,
		billingEndDate: item.billingEndDate,
		duration: item.duration,
		billingCycleUnit: item.billingCycleUnit,
	})),
	totals: {
		subTotal: totals.subTotal,
		discount: totals.discount,
		tax: totals.tax,
		total: totals.total,
	},
	paymentTerms: {
		isIdentical: paymentTermsInfo.isIdentical,
		netTerms: paymentTermsInfo.netTerms,
		billingCycleUnit: paymentTermsInfo.billingCycleUnit,
		productTerms: paymentTermsInfo.productTerms.map((pt) => ({
			productName: pt.productName,
			netTerms: pt.netTerms,
			billingCycleUnit: pt.billingCycleUnit,
		})),
	},
});
